import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, Plus, Edit2, Trash2, CheckCircle, Download, RefreshCw, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEGAL_BASES = [
  { value: 'CONTRACT', label: 'Contratto (Art. 6.1.b)' },
  { value: 'LEGAL_OBLIGATION', label: 'Obbligo legale (Art. 6.1.c)' },
  { value: 'CONSENT', label: 'Consenso (Art. 6.1.a)' },
  { value: 'LEGITIMATE_INTEREST', label: 'Legittimo interesse (Art. 6.1.f)' },
];

const DATA_CATEGORIES = [
  { value: 'ANAGRAFICA', label: 'Dati anagrafici' },
  { value: 'CATASTALE', label: 'Dati catastali' },
  { value: 'BANCARIO', label: 'Dati bancari' },
  { value: 'FISCALE', label: 'Dati fiscali' },
  { value: 'CONTATTO', label: 'Dati di contatto' },
  { value: 'CONTABILE', label: 'Dati contabili' },
  { value: 'DOCUMENTI', label: 'Documenti allegati' },
  { value: 'ALTRO', label: 'Altri dati' },
];

const DATA_SUBJECTS = [
  { value: 'CONDOMINI', label: 'Condomini/Proprietari' },
  { value: 'FORNITORI', label: 'Fornitori' },
  { value: 'DIPENDENTI', label: 'Dipendenti/Portiere' },
  { value: 'PROFESSIONISTI', label: 'Professionisti/Consulenti' },
];

const SECURITY_MEASURES = [
  'RBAC (controllo accessi)',
  'Audit log',
  'Crittografia dati',
  'Backup automatici',
  'Autenticazione a due fattori',
  'Pseudonimizzazione',
];

export default function TreatmentRegistry() {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    purpose: '',
    legal_basis: '',
    data_categories: [],
    data_subjects: [],
    recipients: '',
    retention_period: '',
    security_measures: [],
    processor_roles: '',
    notes: ''
  });

  const fetchRecords = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const url = condoId 
        ? `${API_URL}/api/compliance/treatment-records?condominium_id=${condoId}`
        : `${API_URL}/api/compliance/treatment-records`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setRecords(data.records || []);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nel caricamento');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [token, condoId]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const resetForm = () => {
    setForm({
      title: '',
      purpose: '',
      legal_basis: '',
      data_categories: [],
      data_subjects: [],
      recipients: '',
      retention_period: '',
      security_measures: [],
      processor_roles: '',
      notes: ''
    });
    setEditingRecord(null);
  };

  // Download functions with authentication
  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/compliance/treatment-records/export.csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download fallito');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'registro_trattamenti.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('CSV scaricato');
    } catch (error) {
      toast.error('Errore download CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/compliance/treatment-records/export.pdf?archive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download fallito');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'registro_trattamenti.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF scaricato');
    } catch (error) {
      toast.error('Errore download PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadPrivacyPolicy = async () => {
    setIsDownloading(true);
    try {
      let url = `${API_URL}/api/compliance/privacy-policy/pdf`;
      if (condoId) {
        url += `?condominium_id=${condoId}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download fallito');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'Informativa_Privacy_GDPR.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
      toast.success('Informativa Privacy scaricata');
    } catch (error) {
      toast.error('Errore download Informativa');
    } finally {
      setIsDownloading(false);
    }
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setForm({
      title: record.title || '',
      purpose: record.purpose || '',
      legal_basis: record.legal_basis || '',
      data_categories: record.data_categories || [],
      data_subjects: record.data_subjects || [],
      recipients: (record.recipients || []).join(', '),
      retention_period: record.retention_period || '',
      security_measures: record.security_measures || [],
      processor_roles: (record.processor_roles || []).join(', '),
      notes: record.notes || ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.purpose || !form.legal_basis) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        purpose: form.purpose,
        legal_basis: form.legal_basis,
        data_categories: form.data_categories,
        data_subjects: form.data_subjects,
        recipients: form.recipients.split(',').map(s => s.trim()).filter(Boolean),
        retention_period: form.retention_period,
        security_measures: form.security_measures,
        processor_roles: form.processor_roles.split(',').map(s => s.trim()).filter(Boolean),
        notes: form.notes || null
      };
      
      const url = editingRecord 
        ? `${API_URL}/api/compliance/treatment-records/${editingRecord.id}`
        : `${API_URL}/api/compliance/treatment-records${condoId ? `?condominium_id=${condoId}` : ''}`;
      
      const res = await fetch(url, {
        method: editingRecord ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(editingRecord ? 'Trattamento aggiornato' : 'Trattamento creato');
        setShowDialog(false);
        resetForm();
        fetchRecords();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Eliminare questo trattamento?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/compliance/treatment-records/${recordId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Trattamento eliminato');
        fetchRecords();
      } else {
        toast.error('Errore nella cancellazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleMarkReviewed = async (recordId) => {
    try {
      const res = await fetch(`${API_URL}/api/compliance/treatment-records/${recordId}/review`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Trattamento marcato come revisionato');
        fetchRecords();
      } else {
        toast.error('Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const toggleCategory = (value) => {
    setForm(prev => ({
      ...prev,
      data_categories: prev.data_categories.includes(value)
        ? prev.data_categories.filter(v => v !== value)
        : [...prev.data_categories, value]
    }));
  };

  const toggleSubject = (value) => {
    setForm(prev => ({
      ...prev,
      data_subjects: prev.data_subjects.includes(value)
        ? prev.data_subjects.filter(v => v !== value)
        : [...prev.data_subjects, value]
    }));
  };

  const toggleSecurityMeasure = (value) => {
    setForm(prev => ({
      ...prev,
      security_measures: prev.security_measures.includes(value)
        ? prev.security_measures.filter(v => v !== value)
        : [...prev.security_measures, value]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const getLegalBasisLabel = (value) => {
    return LEGAL_BASES.find(b => b.value === value)?.label || value;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Registro dei Trattamenti"
        subtitle="Privacy e GDPR"
        actions={
          <>
            <Button variant="outline" onClick={fetchRecords} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button variant="outline" onClick={handleDownloadCSV} disabled={isDownloading}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF} disabled={isDownloading}>
            <FileText className="h-4 w-4 mr-2" />
            PDF Registro
          </Button>
          <Button variant="secondary" onClick={handleDownloadPrivacyPolicy} disabled={isDownloading}>
            <Shield className="h-4 w-4 mr-2" />
            Informativa Privacy
          </Button>
            <Button onClick={openNewDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Trattamento
            </Button>
          </>
        }
      />

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Obblighi GDPR</p>
              <p className="text-sm text-blue-700">
                Il Titolare del trattamento deve tenere un registro delle attività di trattamento 
                (Art. 30 GDPR). Questo registro documenta finalità, basi giuridiche, categorie di dati 
                e misure di sicurezza per ogni trattamento.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card>
        <CardHeader>
          <CardTitle>Trattamenti Registrati</CardTitle>
          <CardDescription>
            {records.length} trattament{records.length === 1 ? 'o' : 'i'} nel registro
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nessun trattamento registrato</p>
              <Button variant="outline" className="mt-4" onClick={openNewDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi il primo trattamento
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titolo</TableHead>
                  <TableHead>Base Giuridica</TableHead>
                  <TableHead>Categorie Dati</TableHead>
                  <TableHead>Conservazione</TableHead>
                  <TableHead>Ultima Revisione</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <div className="font-medium">{record.title}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[200px]">
                        {record.purpose}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {getLegalBasisLabel(record.legal_basis)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(record.data_categories || []).slice(0, 2).map(cat => (
                          <Badge key={cat} variant="secondary" className="text-xs">
                            {cat}
                          </Badge>
                        ))}
                        {(record.data_categories || []).length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{record.data_categories.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {record.retention_period || '—'}
                    </TableCell>
                    <TableCell>
                      {record.last_reviewed_at ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          <span className="text-xs">{formatDate(record.last_reviewed_at)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-amber-600">Mai revisionato</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleMarkReviewed(record.id)}
                          title="Marca come revisionato"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEditDialog(record)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(record.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRecord ? 'Modifica Trattamento' : 'Nuovo Trattamento'}
            </DialogTitle>
            <DialogDescription>
              Documenta un'attività di trattamento dati personali
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="title">Titolo del trattamento *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Es. Gestione anagrafiche condominiali"
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label htmlFor="purpose">Finalità del trattamento *</Label>
                <Textarea
                  id="purpose"
                  value={form.purpose}
                  onChange={(e) => setForm(f => ({ ...f, purpose: e.target.value }))}
                  placeholder="Descrivere le finalità per cui i dati vengono trattati"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Base giuridica *</Label>
                <Select 
                  value={form.legal_basis} 
                  onValueChange={(v) => setForm(f => ({ ...f, legal_basis: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_BASES.map(b => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="retention">Periodo di conservazione</Label>
                <Input
                  id="retention"
                  value={form.retention_period}
                  onChange={(e) => setForm(f => ({ ...f, retention_period: e.target.value }))}
                  placeholder="Es. 10 anni"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Categorie di dati</Label>
              <div className="flex flex-wrap gap-2">
                {DATA_CATEGORIES.map(cat => (
                  <Badge
                    key={cat.value}
                    variant={form.data_categories.includes(cat.value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleCategory(cat.value)}
                  >
                    {cat.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Interessati</Label>
              <div className="flex flex-wrap gap-2">
                {DATA_SUBJECTS.map(sub => (
                  <Badge
                    key={sub.value}
                    variant={form.data_subjects.includes(sub.value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleSubject(sub.value)}
                  >
                    {sub.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Misure di sicurezza</Label>
              <div className="flex flex-wrap gap-2">
                {SECURITY_MEASURES.map(measure => (
                  <Badge
                    key={measure}
                    variant={form.security_measures.includes(measure) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleSecurityMeasure(measure)}
                  >
                    {measure}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="recipients">Destinatari (separati da virgola)</Label>
                <Input
                  id="recipients"
                  value={form.recipients}
                  onChange={(e) => setForm(f => ({ ...f, recipients: e.target.value }))}
                  placeholder="Es. Commercialista, Banca"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="processors">Responsabili esterni</Label>
                <Input
                  id="processors"
                  value={form.processor_roles}
                  onChange={(e) => setForm(f => ({ ...f, processor_roles: e.target.value }))}
                  placeholder="Es. Provider hosting"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Note aggiuntive</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Eventuali note o dettagli aggiuntivi"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRecord ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
